'use client';

import clsx from 'clsx/lite';
import { useEffect, useState } from 'react';
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react';

type YoutubeVideo = {
  id: string;
  snippet: {
    thumbnails: {
      high: {
        url: string;
      };
    };
    title: string;
    resourceId: {
      videoId: string;
    };
  };
};

export function VideoSectionClient({
  videos = [],
}: {
  videos: YoutubeVideo[];
}) {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 992);
    handleResize(); // Initialize on mount
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const swiperEnabled = isMobile ? videos.length > 2 : videos.length > 3;

  return (
    <Swiper
      className="video-carousel group lg:!pl-[224px]"
      slidesPerView={'auto'}
      loop={swiperEnabled}
      a11y={{
        enabled: swiperEnabled,
        scrollOnFocus: false,
      }}
      spaceBetween={16}
      breakpoints={{
        // when window width is >= 992px
        992: {
          spaceBetween: 32,
        },
      }}
      // enabled={swiperEnabled}
    >
      {videos.map((video, index) => (
        <SwiperSlide
          key={video.id || index}
          className={clsx(
            'mr-0 w-full max-w-[278px] flex-col items-start justify-start md:max-w-[416px] lg:!h-[452px]',
          )}
          tabIndex={0}
        >
          <div
            className={clsx(
              'right-0 top-0 w-full transition-all duration-300 ease-in-out',
              'lg:absolute lg:h-full lg:[.swiper-slide-active_&]:w-[640px] lg:[.swiper-slide-prev_&]:opacity-0',
            )}
          >
            <div className="relative">
              <img
                src={video.snippet.thumbnails.high.url}
                alt={video.snippet.title}
                className="aspect-video w-full rounded bg-black/20 object-cover"
              />
              <a
                href={`https://www.youtube.com/watch?v=${video.snippet.resourceId.videoId}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-play-bg-2-md js-youtube-modal absolute inset-0"
                role="button"
              />
            </div>
            <h3 className="mt-6 line-clamp-2 max-w-[90%] font-nova text-[20px] font-semibold normal-case leading-[30px] lg:mt-8">
              {video.snippet.title}
            </h3>
          </div>
        </SwiperSlide>
      ))}
      {swiperEnabled && <SlideNextButton />}
    </Swiper>
  );
}

function SlideNextButton() {
  const swiper = useSwiper();

  return (
    <button
      className="absolute bottom-[7px] right-0 z-1 border border-current opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100"
      onClick={() => {
        swiper.slideNext();
      }}
    >
      <svg
        width="56"
        height="56"
        viewBox="0 0 56 56"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        role="presentation"
      >
        <rect width="56" height="56" fill="black" />
        <path
          d="M31.3996 42.8L42.5996 28.4L31.3996 14"
          stroke="white"
          strokeWidth="2"
        />
        <line
          x1="13"
          y1="28.5996"
          x2="41.8"
          y2="28.5996"
          stroke="white"
          strokeWidth="2"
        />
      </svg>
    </button>
  );
}
